/**
 * Biometric Authentication Utility
 * Handles Face ID / Touch ID login for iOS native app
 */

import { logger } from './logger';

// Storage keys
const BIOMETRIC_ENABLED_KEY = 'repconnect_biometric_enabled';
const BIOMETRIC_CREDENTIALS_KEY = 'repconnect_biometric_credentials';

class BiometricAuthService {
  constructor() {
    this.NativeBiometric = null;
    this.isNativeApp = false;
    this.initialized = false;
  }

  /**
   * Initialize the biometric service
   * Must be called before using any other methods
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Check if running in Capacitor native app
      this.isNativeApp = window.Capacitor &&
        window.Capacitor.isNativePlatform &&
        window.Capacitor.isNativePlatform();

      if (this.isNativeApp) {
        // Dynamically import the plugin only in native context
        const { NativeBiometric } = await import('capacitor-native-biometric');
        this.NativeBiometric = NativeBiometric;
        logger.debug('Biometric service initialized for native app');
      } else {
        logger.debug('Biometric service: Not a native app, biometrics disabled');
      }

      this.initialized = true;
    } catch (error) {
      logger.error('Failed to initialize biometric service:', error);
      this.initialized = true; // Mark as initialized even on error to prevent repeated attempts
    }
  }

  /**
   * Check if biometrics are available on this device
   * @returns {Promise<{isAvailable: boolean, biometryType: string}>}
   */
  async isAvailable() {
    await this.initialize();

    if (!this.isNativeApp || !this.NativeBiometric) {
      return { isAvailable: false, biometryType: 'none' };
    }

    try {
      const result = await this.NativeBiometric.isAvailable();
      logger.debug('Biometric availability:', result);
      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType || 'none'
      };
    } catch (error) {
      logger.error('Error checking biometric availability:', error);
      return { isAvailable: false, biometryType: 'none' };
    }
  }

  /**
   * Get the friendly name for the biometry type
   * @param {string} biometryType
   * @returns {string}
   */
  getBiometryName(biometryType) {
    switch (biometryType) {
      case 'FACE_ID':
        return 'Face ID';
      case 'TOUCH_ID':
        return 'Touch ID';
      case 'BIOMETRIC':
        return 'Biometric';
      default:
        return 'Biometric Login';
    }
  }

  /**
   * Check if biometric login is enabled for this user
   * @returns {boolean}
   */
  isBiometricEnabled() {
    return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
  }

  /**
   * Enable biometric login and store credentials securely
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async enableBiometric(email, password) {
    await this.initialize();

    if (!this.isNativeApp || !this.NativeBiometric) {
      return { success: false, error: 'Biometric authentication not available' };
    }

    try {
      // Store credentials securely using the native keychain
      await this.NativeBiometric.setCredentials({
        username: email,
        password: password,
        server: 'com.repconnect.app'
      });

      // Mark biometric as enabled
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
      localStorage.setItem(BIOMETRIC_CREDENTIALS_KEY, email);

      logger.debug('Biometric authentication enabled for:', email);
      return { success: true };
    } catch (error) {
      logger.error('Failed to enable biometric:', error);
      return { success: false, error: error.message || 'Failed to enable biometric login' };
    }
  }

  /**
   * Disable biometric login and remove stored credentials
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async disableBiometric() {
    await this.initialize();

    try {
      if (this.isNativeApp && this.NativeBiometric) {
        // Remove credentials from secure storage
        await this.NativeBiometric.deleteCredentials({
          server: 'com.repconnect.app'
        });
      }

      // Remove local flags
      localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
      localStorage.removeItem(BIOMETRIC_CREDENTIALS_KEY);

      logger.debug('Biometric authentication disabled');
      return { success: true };
    } catch (error) {
      logger.error('Failed to disable biometric:', error);
      // Still remove local flags even if credential deletion fails
      localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
      localStorage.removeItem(BIOMETRIC_CREDENTIALS_KEY);
      return { success: true };
    }
  }

  /**
   * Perform biometric verification and retrieve stored credentials
   * @returns {Promise<{success: boolean, credentials?: {username: string, password: string}, error?: string}>}
   */
  async authenticate() {
    await this.initialize();

    if (!this.isNativeApp || !this.NativeBiometric) {
      return { success: false, error: 'Biometric authentication not available' };
    }

    if (!this.isBiometricEnabled()) {
      return { success: false, error: 'Biometric login not enabled' };
    }

    try {
      // First, verify biometric (Face ID / Touch ID)
      const verified = await this.NativeBiometric.verifyIdentity({
        reason: 'Log in to RepConnect',
        title: 'Sign In',
        subtitle: 'Use biometrics to sign in',
        description: 'Touch the fingerprint sensor or look at your device'
      });

      logger.debug('Biometric verification result:', verified);

      // If verification successful, retrieve credentials
      const credentials = await this.NativeBiometric.getCredentials({
        server: 'com.repconnect.app'
      });

      if (credentials && credentials.username && credentials.password) {
        return {
          success: true,
          credentials: {
            username: credentials.username,
            password: credentials.password
          }
        };
      } else {
        return { success: false, error: 'No stored credentials found' };
      }
    } catch (error) {
      logger.error('Biometric authentication failed:', error);

      // Handle specific error cases
      if (error.message?.includes('cancelled') || error.code === 'UserCancel') {
        return { success: false, error: 'Authentication cancelled', cancelled: true };
      }

      return { success: false, error: error.message || 'Biometric authentication failed' };
    }
  }

  /**
   * Get the stored email for biometric login (for display purposes)
   * @returns {string|null}
   */
  getStoredEmail() {
    return localStorage.getItem(BIOMETRIC_CREDENTIALS_KEY);
  }

  /**
   * Check if we should show the biometric login option
   * @returns {Promise<boolean>}
   */
  async shouldShowBiometricLogin() {
    const { isAvailable } = await this.isAvailable();
    return isAvailable && this.isBiometricEnabled();
  }
}

// Export singleton instance
export const biometricAuth = new BiometricAuthService();
export default biometricAuth;
